import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import { ActiveStore, StoreContextService } from '../services/store-context.service';
import { StorageKey, StorageService } from '../services/storage.service';
import { HubDeviceService } from './hub-device.service';

/**
 * Which pages may use the Offline Hub, in one place for the four things
 * that ask: hubBaseUrlInterceptor (does a request get rewritten to the
 * hub?), HubBootstrapService (does this page start hub discovery?),
 * error.interceptor (may a failed request start the fast drop
 * confirmation?) and NetworkStatusService (does recovery need RECON-1's
 * full proof? — RECON-18).
 */

/**
 * OF-FIX-C7 — surfaces with no hub mirror behind them: kiosk, KDS, pickup
 * display, customer display, and their "unavailable" placeholders. A
 * DENY-list on purpose: a surface missing from it keeps today's behaviour,
 * whereas an allow-list that missed a POS route would silently disable
 * offline for it. Matches `modeToUrl()` in core/utils/mode-routing.ts.
 * Anchored on whole path segments, so `/pos/kiosk-settings` is still POS.
 */
const HUBLESS_SEGMENT =
  /(^|\/)(kiosk|kds|kds-unavailable|pickup-display|customer-display|customer-display-unavailable)(\/|$|\?|#)/;

/**
 * PTM D7 — the Payment Terminal is online-only and never uses the hub: its
 * working screens (`/pos/pt/…`) and its PIN entry (`/pos/s/<code>/pt`).
 */
export const PAYMENT_TERMINAL_ROUTE = /^\/pos\/(pt|s\/[^/?#]+\/pt)(\/|$|\?|#)/;

/**
 * Every surface whose requests must never go to the hub: the C7 deny-list
 * plus the Payment Terminal's routes (OFP-082 review). Tested against both
 * `router.url` and `location.pathname`.
 */
export const NON_POS_SURFACE = new RegExp(`${HUBLESS_SEGMENT.source}|${PAYMENT_TERMINAL_ROUTE.source}`);

/** The path part of a router URL or pathname: no query string, no fragment. */
export function pathOf(url: string | null | undefined): string {
  return (url ?? '').split('?')[0].split('#')[0];
}

/**
 * OFP-082 review — a route where the hub may serve: `/pos` or under it,
 * minus the Payment Terminal and any hubless surface — and `/`, which the
 * router sends to `/pos` (app.routes.ts), so a page loaded there is a POS
 * page (OFP-082 re-review). An ALLOW-list, unlike the request rule above,
 * because it decides where hub discovery STARTS: login, store selection,
 * device registration and tenant routes never needed it, and with a cached
 * offline store and a hub backlog the drain hold would otherwise send their
 * calls to the hub. (AppComponent's `isPosOperatorRoute` is a different,
 * wider rule: where the offline banner shows.)
 */
export function isHubCapablePath(url: string | null | undefined): boolean {
  const path = pathOf(url);
  if (path === '/' || path === '') return true;
  if (path !== '/pos' && !path.startsWith('/pos/')) return false;
  return !NON_POS_SURFACE.test(path);
}

/**
 * OFP-082 review — could a hub take over if the cloud were gone? Only then
 * is a failed request allowed to start the fast drop confirmation: on a POS
 * operator route, not on a Payment Terminal device, and either THIS device
 * is the hub or the store has offline on with a hub URL. Everywhere else a
 * false "offline" buys nothing: an offline-disabled store gets the
 * full-screen lock, and a Payment Terminal pauses payments.
 *
 * RECON-18 — and only then does recovery need RECON-1's full proof (5
 * probes over 60 s). Everywhere else two successful probes are enough, and
 * the D1 lock's Retry unlocks on one (NetworkStatusService).
 */
export function hubCanTakeOver(ctx: {
  url: string | null | undefined;
  paytermDevice: boolean;
  isHubDevice: boolean;
  store: ActiveStore | null | undefined;
}): boolean {
  if (ctx.paytermDevice || !isHubCapablePath(ctx.url)) return false;
  if (ctx.isHubDevice) return true;
  return ctx.store?.offlineEnabled === true && !!ctx.store.offlineHubUrl?.trim();
}

/**
 * `hubCanTakeOver` for this page right now, read from the injector:
 * error.interceptor asks it for every failed request, NetworkStatusService
 * (RECON-18) for every successful probe while offline. Missing services
 * read as "no", like the interceptor always did.
 */
export function hubCanTakeOverHere(injector: Injector): boolean {
  const router = injector.get(Router, null);
  const storage = injector.get(StorageService, null);
  const store = injector.get(StoreContextService, null);
  const hubDevice = injector.get(HubDeviceService, null);
  const url = router?.url && router.url !== '/'
    ? router.url
    : (typeof window !== 'undefined' ? window.location.pathname : '');
  return hubCanTakeOver({
    url,
    paytermDevice: !!(storage?.getRaw(StorageKey.PAYTERM_DEVICE) ?? '').trim(),
    isHubDevice: hubDevice?.isHubDevice() ?? false,
    store: store?.activeStore ?? null,
  });
}
